import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'

import * as types from '@/vuex/types'

import api from '@/api'
import { apiV2 } from '@/api/urls'

// Modules
import devices from '@/vuex/modules/devices'
import saved from '@/vuex/modules/saved'

import { defaultSavedConfig } from '@/lib/config'

Vue.use(Vuex)

const state = {
  sm: 575,
  md: 769,
  lg: 991,
  user: '',
  defaultSaved: null,
  finalBalance: null,
  account: {
    token: '',
    isSuperuser: '',
    isStaff: '',
    groups: '',
    userId: '',
    profile: ''
  },
  hijacked: false,
  hijacker: {
    token: '',
    isSuperuser: '',
    isStaff: '',
    groups: '',
    userId: '',
    profile: ''
  }
}

const getters = {
  user: state => state.user,
  defaultSaved: state => state.defaultSaved,
  account: state => state.account,
  token: state => state.account.token,
  userId: state => state.account.userId,
  profileImage: state => {
    const profile = state.account.profile
    if (typeof profile === 'object') {
      const { avatar } = profile
      return avatar !== undefined ? avatar : false
    } else {
      return false
    }
  },
  isSuperuser: state => state.account.isSuperuser,
  finalBalance: state => state.finalBalance,
  isStaff: state => state.account.isStaff,
  groups: state => state.account.groups,
  hijacked: state => state.hijacked,
  hijacker: state => state.hijacker,
  profile: state => state.account.profile || null
}

const mutations = {
  [types.GET_ACCOUNT] (state, response) {
    state.account = response
  },
  [types.GET_PROFILE] (state, response) {
    state.account.profile = response
  },
  [types.REMOVE_ACCOUNT] (state) {
    const defaultState = {
      'token': '',
      'isSuperuser': '',
      'isStaff': '',
      'groups': '',
      'userId': '',
      'profile': ''
    }

    state.account = defaultState
  },
  [types.DEFAULT_SAVED] (state, response) {

    state.defaultSaved = response
  },
  [types.SET_TOKEN] (state, token) {
    state.account.token = token
  },
  [types.GET_FINAL_BALANCE] (state, response) {
    state.finalBalance = response
  },
  [types.SET_HIJACKER] (state) {
    state.hijacker = state.account
  },
  [types.SET_HIJACKED] (state, data) {
    state.hijacked = data
  },
  [types.REVERT_ACCOUNT] (state) {
    state.account = state.hijacker
  },
  [types.REMOVE_APOLLO_AUTH] (state) {
    localStorage.removeItem('apollo-token')
  },
  [types.ADD_APOLLO_AUTH](state) {
    localStorage.setItem('apollo-token', state.account.token);
  },
  [types.REMOVE_HIJACKER] (state) {
    const defaultState = {
      token: '',
      isSuperuser: '',
      isStaff: '',
      groups: '',
      userId: '',
      profile: ''
    }
    state.hijacker = defaultState
  }
}

const actions = {
  async getAccount ({ commit }, payload) {
    commit(types.GET_ACCOUNT, payload)
  },
  async removeAccount ({ dispatch, commit }, payload) {
    commit(types.REMOVE_ACCOUNT)
    await dispatch(`removeApolloToken`)
  },
  async checkToken ({ commit }) {
    const response = await api.get(apiV2.authCheck, { needToken: true })
    if (response.status === 200) {
      commit(types.GET_ACCOUNT, response.data)
    }
  },
  async setProfile ({ dispatch, commit }) {
    const responseProfile = await api.get(apiV2.profileMe, { needToken: true })
    if (responseProfile.status === 200) {
      await commit(types.GET_PROFILE, responseProfile.data.profile)
      await dispatch(`setApolloToken`)
    }
  },
  async setTokenFromSocial ({ dispatch, commit }, payload) {
    const { status, data } = payload
    if (status === 200) {
      commit(types.SET_TOKEN, data.token)
      await dispatch(`checkToken`)
      await dispatch(`setProfile`)
    }
  },
  async tokenFromActivation ({ commit }, payload) {
    commit(types.SET_TOKEN, payload.token)
  },
  async getFinalBalance ({ commit }) {
    const response = await api.get(apiV2.finalBalance, { needToken: true })
    await commit(types.GET_FINAL_BALANCE, response.data.balance)
  },
  async setHijacked ({ commit }, payload = true) {
    commit(types.SET_HIJACKED, payload)
  },
  async setAccountAsHijacker ({ commit }) {
    commit(types.SET_HIJACKER)
  },
  async revertAccount ({ commit }) {
    commit(types.REVERT_ACCOUNT)
  },
  async removeHijacker ({ commit }) {
    commit(types.REMOVE_HIJACKER)
  },
  async removeApolloToken ({ commit }) {
    commit(types.REMOVE_APOLLO_AUTH)
  },
  async setApolloToken ({ commit }) {
    commit(types.ADD_APOLLO_AUTH)
  },
  async startHijack ({ dispatch, commit }, payload) {
    // Move existing to hijacker
    await dispatch(`setAccountAsHijacker`)
    // Set response to account
    await dispatch(`getAccount`, payload)
    // Get profile to account
    await dispatch(`setProfile`)
    // Set hijacked to true
    await dispatch(`setHijacked`)
  },
  async releaseHijack ({ dispatch, commit }) {
    // Set hijacked to false
    await dispatch(`setHijacked`, false)
    // Remove Account
    await dispatch(`removeAccount`)
    // Move Back From Hijacker to Account
    await dispatch(`revertAccount`)
    // Empty Hijacker
    await dispatch(`removeHijacker`)
    // Set Profile
    await dispatch(`setProfile`)
  }
}

export default new Vuex.Store({
  state,
  getters,
  mutations,
  actions,
  plugins: [createPersistedState({
    reducer: state => ({
      account: state.account,
      hijacker: state.hijacker,
      hijacked: state.hijacked
    })
  })],
  modules: {
    devices,
    saved
  }
})
